import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Autocomplete,
  Box,
  Checkbox,
  DatePicker,
  FormControlLabel,
  FormInputText,
  InputLabel,
  RadioButton,
  RadioGroup,
  Text,
} from '@hopskipdrive/react-components';
import { DATE_FORMAT_FOR_API } from '@rideiq/constants';
import { useAccountLocationsSearch } from '@rideiq/hooks/useAccountLocationsSearch';
import { useTimezone } from '@rideiq/hooks/useTimezone';
import { RiderLocation } from '@rideiq/riders/features/RiderLocations/Location';
import { colorPalette, colorTokens } from '@rideiq/shared/components/variables';
import { addDays, format, isValid, parseISO } from 'date-fns';

import { PageHeading } from '../../../../shared/components/PageHeading';
import { RequiredAsterik } from '../styles';

const ID_FIELD_KEY = 'replaceLocation.id';
const EFFECTIVE_AT_FIELD_KEY = 'replaceLocation.effectiveAt';
const EFFECTIVE_IN_FUTURE = 'future';
const RIDER_ASSIGNED_LOCATION_GROUP = 'Rider Assigned Locations:';
const SAVED_LOCATION_GROUP = 'Saved locations:';

type LocationForRider = Pick<
  RiderLocation,
  'id' | 'placeId' | 'name' | 'address' | 'replaceLocation' | 'schedulingDetails'
>;

type LocationOption = {
  group: string;
} & LocationForRider;

interface ReplaceLocationType {
  id: Nullable<number>;
  effectiveAt: Nullable<string | Date>;
}

const isSameLocationId = (a: unknown, b: unknown): boolean => Number(a) === Number(b);

const findLocationsWithPendingReplacement = (
  associatedLocations: LocationForRider[],
  excludingLocationId: number | undefined
): Set<number> => {
  const ids = new Set<number>();

  associatedLocations.forEach((loc) => {
    if (isSameLocationId(loc.id, excludingLocationId)) {
      return;
    }

    if (loc.replaceLocation?.id) {
      ids.add(Number(loc.replaceLocation.id));
      ids.add(Number(loc.id));
    }

    if (loc.schedulingDetails) {
      ids.add(Number(loc.id));
    }
  });

  return ids;
};

export const FindAndReplace = ({
  associatedLocations,
  locationIdToReplace,
  onToggle,
  hideSavedLocations = false,
}: {
  associatedLocations: LocationForRider[];
  locationIdToReplace: number | undefined;
  onToggle?: (value: boolean) => void;
  hideSavedLocations?: boolean;
}) => {
  return (
    <Presentation
      associatedLocations={associatedLocations}
      locationIdToReplace={locationIdToReplace}
      onToggle={onToggle}
      hideSavedLocations={hideSavedLocations}
    />
  );
};

const Presentation = ({
  associatedLocations,
  locationIdToReplace,
  onToggle,
  hideSavedLocations,
}: {
  associatedLocations: LocationForRider[];
  locationIdToReplace: number | undefined;
  onToggle?: (value: boolean) => void;
  hideSavedLocations: boolean;
}) => {
  const { getValues } = useFormContext<{ replaceLocation?: ReplaceLocationType }>();
  const [showForm, setShowForm] = useState(() => !!getValues('replaceLocation')?.id);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }
    setShowForm(false);
  }, [locationIdToReplace]);

  return (
    <div>
      <PageHeading variant="h2" sx={{ margin: '16px 0px', color: `${colorTokens.primary.main} !important` }}>
        Find and replace
      </PageHeading>

      <label style={{ display: 'flex', alignItems: 'center', fontWeight: 300 }}>
        <Checkbox
          onChange={(e) => {
            setShowForm(e.target.checked);
            if (onToggle) {
              onToggle(e.target.checked);
            }
          }}
          checked={showForm}
        />
        {'Find and replace previously used locations'}
      </label>
      {showForm && (
        <FindAndReplaceForm
          associatedLocations={associatedLocations}
          locationIdToReplace={locationIdToReplace}
          hideSavedLocations={hideSavedLocations}
        />
      )}
    </div>
  );
};

const FindAndReplaceForm = ({
  associatedLocations,
  locationIdToReplace,
  hideSavedLocations,
}: {
  associatedLocations: LocationForRider[];
  locationIdToReplace: number | undefined;
  hideSavedLocations: boolean;
}) => {
  const { locations, debounceHandleQuery, isLoading } = useAccountLocationsSearch();
  const {
    setValue,
    control,
    getValues,
    formState: { errors },
  } = useFormContext<{ replaceLocation: ReplaceLocationType }>();
  const { getDateInTimezone } = useTimezone();
  const [dateInputValue, setDateInputValue] = useState<Nullable<Date>>(() => {
    const existingEffectiveAt = getValues(EFFECTIVE_AT_FIELD_KEY);
    const today = format(new Date(), DATE_FORMAT_FOR_API);

    if (!existingEffectiveAt || existingEffectiveAt === today) {
      return null;
    }

    const parsedDate = typeof existingEffectiveAt === 'string' ? parseISO(existingEffectiveAt) : existingEffectiveAt;

    return isValid(parsedDate) ? parsedDate : null;
  });
  const locationsWithPendingReplacement = findLocationsWithPendingReplacement(associatedLocations, locationIdToReplace);

  const idsOfAssociatedLocations: number[] = [];
  const associatedLocationsOptions: LocationOption[] = associatedLocations
    .filter((loc) => !locationsWithPendingReplacement.has(Number(loc.id)))
    .map((loc) => {
      idsOfAssociatedLocations.push(Number(loc.id));

      return { group: RIDER_ASSIGNED_LOCATION_GROUP, ...loc };
    });

  const filteredLocationOptions: LocationOption[] = hideSavedLocations
    ? []
    : locations
        .map((loc) => ({ group: SAVED_LOCATION_GROUP, ...loc }))
        .filter((loc) => !idsOfAssociatedLocations.includes(Number(loc.id)));

  const allOptions = [...associatedLocationsOptions, ...filteredLocationOptions];

  const onChangeEffectiveAt = (value: Date | null) => {
    value &&
      isValid(value) &&
      setValue(EFFECTIVE_AT_FIELD_KEY, format(value, DATE_FORMAT_FOR_API), {
        shouldValidate: true,
        shouldDirty: true,
      });
    isValid(value) && setDateInputValue(value);
  };

  return (
    <>
      <Box sx={{ margin: '16px 0px' }}>
        <Text variant="body2" textColor="secondaryText" fontWeight={300}>
          <InputLabel fieldtype="required" htmlFor={ID_FIELD_KEY}>
            {'Select location to replace'}
          </InputLabel>
        </Text>
        <Controller
          control={control}
          name={ID_FIELD_KEY}
          defaultValue={null}
          render={({ field: { onChange, value } }) => {
            const selectedOption = allOptions.find((option) => isSameLocationId(option.id, value)) ?? null;

            return (
              <Autocomplete
                id={ID_FIELD_KEY}
                freeSolo
                value={selectedOption}
                options={allOptions}
                filterOptions={
                  hideSavedLocations
                    ? undefined
                    : (options, state) => filterIfInputPresent(options, state.inputValue, locations)
                }
                groupBy={(option) => option?.group}
                getOptionDisabled={(option) => isSameLocationId(option.id, locationIdToReplace)}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : getAddressName(option.name, option.address) || ''
                }
                onChange={(_, newValue) =>
                  !newValue ? onChange(newValue) : newValue && typeof newValue !== 'string' && onChange(newValue.id)
                }
                loading={!hideSavedLocations && isLoading}
                isOptionEqualToValue={(option, optionValue) => isSameLocationId(option.id, optionValue.id)}
                renderInput={(params) => (
                  <FormInputText
                    {...params}
                    variant="outlined"
                    placeholder="Select"
                    onChange={(e) => {
                      if (!hideSavedLocations) {
                        debounceHandleQuery(e.target.value);
                      }
                    }}
                    error={!!errors.replaceLocation?.id}
                    helperText={errors.replaceLocation?.id ? 'Required Field' : ''}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {getAddressName(option.name, option.address)}
                  </li>
                )}
              />
            );
          }}
        />
      </Box>
      <PageHeading variant="h2" mt={'16px'}>
        Starting
        <RequiredAsterik />
      </PageHeading>
      <Controller
        control={control}
        name={EFFECTIVE_AT_FIELD_KEY}
        defaultValue={null}
        render={({ field: { onChange, value } }) => {
          const today = format(new Date(), DATE_FORMAT_FOR_API);
          const effectiveAtNeedsDateSelected = errors.replaceLocation?.effectiveAt && value === EFFECTIVE_IN_FUTURE;
          const noValueSelected = errors.replaceLocation?.effectiveAt && value !== EFFECTIVE_IN_FUTURE;

          return (
            <RadioGroup
              error={noValueSelected ? errors.replaceLocation?.effectiveAt?.message : ''}
              sx={{ marginTop: '16px' }}
              value={value}
              onChange={(e) => {
                onChange(e);
                dateInputValue &&
                  e.target.value === EFFECTIVE_IN_FUTURE &&
                  setValue(EFFECTIVE_AT_FIELD_KEY, dateInputValue, { shouldValidate: true, shouldDirty: true });
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  padding: '8px',
                  borderRadius: '16px',
                  border: noValueSelected ? `1px solid ${colorPalette.red55}` : 'none',
                }}
              >
                <FormControlLabel value={today} control={<RadioButton />} label={<EffectiveImmediatelyLabel />} />
                <FormControlLabel
                  // value is either 'future' or the value from date picker
                  value={value && value !== today ? value : EFFECTIVE_IN_FUTURE}
                  control={<RadioButton />}
                  label={'On a future date'}
                />
                <DatePicker
                  value={dateInputValue}
                  disabled={!!value && value === today}
                  onChange={onChangeEffectiveAt}
                  onAccept={onChangeEffectiveAt}
                  minDate={getDateInTimezone(addDays(new Date(), 1))}
                  slotProps={{
                    textField: {
                      error: effectiveAtNeedsDateSelected,
                      helperText: effectiveAtNeedsDateSelected ? 'Required Field' : '',
                    },
                  }}
                  sx={{ width: '200px', marginLeft: '16px' }}
                />
              </Box>
            </RadioGroup>
          );
        }}
      />
    </>
  );
};

const getAddressName = (name: string, address: string) => (name ? `${name}, ${address}` : address);

const EffectiveImmediatelyLabel = () => (
  <>
    <span>{'Immediately '}</span>
    <Box sx={{ fontWeight: '200', display: 'inline-block' }}>{'(Next upcoming ride)'}</Box>
  </>
);

// we already have the entire list of associate locations, and we get a list of locations from the api, so we can filter out
export const filterIfInputPresent = (
  options: LocationOption[],
  inputValue: string,
  locations: AccountLocation[]
): LocationOption[] => {
  const idsOfLocationsReturnedFromApi = locations.map((loc) => loc.id);

  return inputValue
    ? options.filter((o) => {
        if (o.group === RIDER_ASSIGNED_LOCATION_GROUP) {
          return idsOfLocationsReturnedFromApi.includes(o.id);
        }

        return true;
      })
    : options;
};
